// ============================================================================
// peanutPercSystem.ts — GOOP ZONE Peanut Percs In-Run System (v3)
// Manages in-run tome slots, level-up selection, and stat accumulation
// Lives in: client/src/game/peanutPercSystem.ts
// ============================================================================

import { rollRng } from './rng'; // BETA 20.92 — seeded balance-roll RNG (reproducible level-up offers)
import {
  PeanutPerc,
  PercStat,
  UpgradeRarity,
  getAllPercs,
  getPercById,
  calculateUpgradeBonus,
  formatPercBonus,
  luckValueMult,
  PEANUT_PERCS,
} from './peanutPercs';
// BETA 22.87 — PERK CARD SYSTEM v1: 4-tier card rarity + card magnitudes +
// family metadata live in perkCards.ts (data-driven; Dylan's spec).
import {
  rollCardRarity,
  calculateCardBonus,
  LEVELUP_EXCLUDED_PERC_IDS,
  CARD_FAMILY_BY_PERC,
} from './perkCards';

// --- Constants ---

/** Starting number of perc slots for all players.
 *  BETA 15.5 — was 2, raised to 4 per Dylan: "I'm only allowed to get four
 *  different perks. And once I get four different perks, those are the only
 *  perks I'm allowed to get." Every run now starts at the cap (no GOOP-shop
 *  unlock needed). After 4 unique perks are picked, the existing
 *  generateLevelUpOptions() logic only offers upgrades to those 4. */
export const DEFAULT_PERC_SLOTS = 4;

/** Maximum number of perc slots (hard cap — Dylan's "4 different perks" rule). */
export const MAX_PERC_SLOTS = 4;

/** Number of upgrade options shown at level-up */
export const LEVEL_UP_OPTIONS_COUNT = 3;

/** XP required base constant. Scales with each level.
 *  BETA 20.66 — 15 → 30 (with POWER 1.6 → 2.2 below). Charter §3: picks
 *  every 10-15s early → 30-45s by min 10. The old curve served a pick every
 *  8-13s for the ENTIRE run (level 49 at 7 min — the "menu simulator").
 *  Verified model: ~L16 @ 5min, ~L24 @ 10min; first pick still inside ~10s. */
export const BASE_XP_REQUIRED = 30;

/** BETA 18.18 — XP scaling factor (LEGACY LINEAR). Kept for historical
 *  reference only. Was `xpRequired = BASE * (1 + level * 0.35)` for the
 *  peanutPercSystem internal shadow tracker. Never actually controlled
 *  game leveling — engine.ts had its own inline `15 * level^1.6` formula.
 *  BETA 18.19 consolidated: this file is now the single source of truth
 *  and the engine calls xpRequiredForLevel() instead of its inline math. */
export const XP_SCALE_FACTOR = 0.35;

/** BETA 18.19 — Power exponent for the genre-standard sublinear-exponential
 *  curve `BASE * level^POWER`. Matches the original engine.ts formula from
 *  before consolidation so existing balance is preserved.
 *
 *  Sample values with BASE=30, POWER=2.2 (BETA 20.66 steepening):
 *    lvl 1   → 30 XP
 *    lvl 5   → 1,034 XP
 *    lvl 10  → 4,757 XP
 *    lvl 24  → ~32,400 XP   (the ~10-minute mark per the verified model)
 *    lvl 50  → ~164,000 XP
 *  Late levels are EARNED — a pick every 30-45s at min 10 instead of the
 *  old every-8-13s conveyor (Charter §3; VS-style braking, fast early). */
export const XP_POWER_EXPONENT = 2.2; // BETA 20.66 — 1.6 → 2.2 (see BASE_XP_REQUIRED note)

/** BETA 18.18-shipped-but-unused. Geometric growth alternative. Was too
 *  soft (lvl 50 needed only 114 XP) — gentle linear early-game collapse.
 *  Kept as a tuning knob in case we ever want to revisit. */
export const XP_GROWTH_RATE = 1.05;

// BETA 14.73 — BUG-5 fix: percTracker.applies wiring for bot/real-game picks.
// Previously only weaponLab.incrementPercLevel called trackPercApply — bot
// picks via selectLevelUpOption silently bypassed the tracker, leaving
// percTracker entries with applies=0 in BotFarm CSV runs. Same registration
// pattern as registerShrineSelectTracker (engine.ts wires it on init to avoid
// circular deps with weaponLab).
type PercApplyTracker = (percId: string, newStack: number) => void;
let _percApplyTracker: PercApplyTracker | null = null;
export function registerPercApplyTracker(t: PercApplyTracker | null): void {
  _percApplyTracker = t;
}

// BETA 15.34 — Pick audit callback. Fires alongside the apply tracker but
// receives the FULL offer set + the picked option so engine.ts can record
// the picked + offered cards into the audit log with player stat snapshot.
// Separate callback (not extending PercApplyTracker) to avoid the BETA 15.24
// freeze pattern of stuffing extra state into the apply path.
//
// Engine handler reads `option` + `allOptions` and writes a PickAuditEntry.
// LevelUpOption defined later in this file — use a forward `any` here to
// avoid awkward forward-reference. The callback parameters get correct
// types at call site (line ~432).
let _percPickAuditor: ((option: LevelUpOption, allOptions: LevelUpOption[]) => void) | null = null;
export function registerPercPickAuditor(cb: ((option: LevelUpOption, allOptions: LevelUpOption[]) => void) | null): void {
  _percPickAuditor = cb;
}

// BETA 15.31 — Per-run perc-pick rarity counters. Per Dylan: "I need to
// understand if luck is actually steering offers." Cross-reference at
// finalizeRun: AVG(legendaryPicks) GROUP BY finalLuckPct bucket. If higher
// luck → more legendary picks, system works. If flat correlation, broken.
let _percPickCommon = 0;
let _percPickUncommon = 0;
let _percPickRare = 0;
let _percPickEpic = 0;
let _percPickLegendary = 0;
let _percPickTotal = 0;
export function getPercPickRarityCounts(): {
  common: number; uncommon: number; rare: number; epic: number; legendary: number; total: number;
} {
  return {
    common: _percPickCommon, uncommon: _percPickUncommon, rare: _percPickRare,
    epic: _percPickEpic, legendary: _percPickLegendary, total: _percPickTotal,
  };
}
export function resetPercPickRarityCounts(): void {
  _percPickCommon = 0; _percPickUncommon = 0; _percPickRare = 0;
  _percPickEpic = 0; _percPickLegendary = 0; _percPickTotal = 0;
}

// --- In-Run Perc Slot ---

export interface PercSlot {
  /** The perc occupying this slot (null if empty) */
  perc: PeanutPerc | null;
  /** Current level of this perc in this run (0 if empty) */
  level: number;
  /** Accumulated total bonus (sum of all upgrade bonuses with rarity applied) */
  accumulatedBonus: number;
}

// --- Level-Up Upgrade Option ---

export interface LevelUpOption {
  /** Type of upgrade: 'new_perc' to fill an empty slot, 'upgrade_perc' to level existing */
  type: 'new_perc' | 'upgrade_perc';
  /** The perc this option refers to */
  perc: PeanutPerc;
  /** The rarity of this specific upgrade offer */
  rarity: UpgradeRarity;
  /** The stat bonus this specific pick would give */
  bonusAmount: number;
  /** Formatted display string for the bonus */
  bonusDisplay: string;
  /** For 'upgrade_perc': the slot index being upgraded. For 'new_perc': -1 */
  slotIndex: number;
  /** For 'upgrade_perc': what the new level would be after picking this */
  resultLevel: number;
  /** BETA 21.04 — true when LUCK raised this pick's number above base → 🍀 badge. */
  luckBoosted?: boolean;
}

// --- XP & Leveling State ---

export interface LevelingState {
  /** Current player level (starts at 1) */
  level: number;
  /** Current XP accumulated toward next level */
  currentXp: number;
  /** XP required to reach the next level */
  xpToNextLevel: number;
  /** Total XP earned this run */
  totalXpEarned: number;
}

// --- Peanut Perc In-Run System State ---

export interface PeanutPercRunState {
  /** The perc slots (length = number of unlocked slots) */
  slots: PercSlot[];
  /** Maximum slots available (2-4) */
  maxSlots: number;
  /** Set of perc IDs that are unlocked on this account (from meta-progression) */
  unlockedPercIds: Set<string>;
  /** Set of perc IDs that have been banished this run */
  banishedPercIds: Set<string>;
  /** Leveling state */
  leveling: LevelingState;
  /** Whether the level-up screen is currently showing */
  isLevelUpActive: boolean;
  /** Current level-up options being presented (null if not leveling up) */
  currentOptions: LevelUpOption[] | null;
  /** Perk IDs already shown this level-up session — reroll skips these first */
  offeredThisLevelUp: Set<string>;
  /** BETA 16.20 — PSBOT cycle-23 P1.1: anti-repeat queue. Tracks the last
   *  6 perc IDs offered across recent level-ups (~2 level-ups worth of
   *  3-card offers). New offers filter against this so players can't
   *  tunnel-vision into one stat over many consecutive levels.
   *  Per cycle-23: high-luck runs (Dylan at 81.7% luck) saw the same
   *  statistical favorites (Steroids/Vitamins/Adderall) repeat heavily —
   *  one player stacked +damage 50+ times. Queue forces variety.
   *  If filtered pool drops below 3, dedup is loosened so we don't
   *  soft-lock at low pool sizes. */
  recentlyOfferedPercs: string[];
}

// --- Initialize System ---

/**
 * Create a fresh in-run Peanut Perc state.
 * @param unlockedPercIds - Set of perc IDs unlocked on this account
 * @param percSlotCount - Number of perc slots unlocked (2-4)
 */
export function createPercRunState(
  unlockedPercIds: Set<string>,
  percSlotCount: number = DEFAULT_PERC_SLOTS
): PeanutPercRunState {
  const slotCount = Math.min(Math.max(percSlotCount, DEFAULT_PERC_SLOTS), MAX_PERC_SLOTS);
  const slots: PercSlot[] = [];
  for (let i = 0; i < slotCount; i++) {
    slots.push({ perc: null, level: 0, accumulatedBonus: 0 });
  }

  return {
    slots,
    maxSlots: slotCount,
    unlockedPercIds,
    banishedPercIds: new Set(),
    leveling: {
      level: 1,
      currentXp: 0,
      xpToNextLevel: BASE_XP_REQUIRED,
      totalXpEarned: 0,
    },
    isLevelUpActive: false,
    currentOptions: null,
    offeredThisLevelUp: new Set(),
    recentlyOfferedPercs: [],
  };
}

// --- XP & Level-Up ---

/**
 * Calculate XP required for a given level.
 *
 * BETA 18.19 — Single source of truth. Formula `BASE * level^POWER` matches
 * the genre standard (sublinear-exponential) and matches the original
 * engine.ts inline formula that was the actual game-leveling math
 * (peanutPercSystem only had a shadow tracker prior to 18.19). Engine now
 * calls this function instead of computing `15 * level^1.6` inline so any
 * future curve tuning happens in ONE place.
 *
 * BETA 18.18 — Was BASE * 1.05^level (geometric). Way too soft;
 * lvl 50 only needed 114 XP. Reverted to the engine's original formula
 * which is properly steep (lvl 50 = 8,666 XP, lvl 100 = 26,250 XP).
 *
 * Special case: lvl 0 → 1 (the very first level) returns BASE flat so the
 * first level is reachable on minute 1 of any run regardless of build.
 */
export function xpRequiredForLevel(level: number): number {
  if (level <= 0) return BASE_XP_REQUIRED;
  return Math.floor(BASE_XP_REQUIRED * Math.pow(level, XP_POWER_EXPONENT));
}

/**
 * Add XP to the player. Returns true if a level-up occurred.
 * The engine should call this whenever XP orbs are collected.
 * @param xpGainMultiplier - The player's total XP gain multiplier (from percs + items)
 */
export function addXp(
  state: PeanutPercRunState,
  rawXp: number,
  xpGainMultiplier: number = 1.0
): boolean {
  const xp = Math.floor(rawXp * xpGainMultiplier);
  state.leveling.currentXp += xp;
  state.leveling.totalXpEarned += xp;

  if (state.leveling.currentXp >= state.leveling.xpToNextLevel) {
    // Level up!
    state.leveling.currentXp -= state.leveling.xpToNextLevel;
    state.leveling.level++;
    state.leveling.xpToNextLevel = xpRequiredForLevel(state.leveling.level);
    return true;
  }

  return false;
}

// --- Rarity Rolling ---

/**
 * Roll a random upgrade rarity based on the player's luck stat.
 * Higher luck = higher chance of rare+ upgrades.
 * @param luck - Player's current luck value (0 = base, higher = better)
 */
export function rollUpgradeRarity(luck: number = 0): UpgradeRarity {
  // BETA 16.27 — PSBOT cycle-30 C.2: rarity curve flattened so visible
  // legendary boost kicks in at 25-50% luck (was 75-100%). Per Megabonk
  // research + Goopzone's pacing (median final luck = 0%, only 5% of
  // runs reach 50%+ luck), the old curve made luck investment basically
  // invisible. New: shallower base, wider luck-influence range, higher
  // ceiling at extreme investment.
  //
  // Base (luck=0): Legendary 0.5%, Epic 3%, Rare 12%, Uncommon 30%, Common 54.5%
  // At 25% luck: Legendary 4.5%, Epic 8%, Rare 17%
  // At 50% luck: Legendary 8.5%, Epic 13%, Rare 22%
  // At 100% luck: Legendary 16.5%, Epic 23%, Rare 32% (cap zone)
  // At 200%+ luck: Legendary 20%, Epic 28%, Rare 37% (capped)
  // BETA 20.21 — 3-TIER (Dylan): common / rare / legendary ONLY. uncommon + epic
  // retired (their old probability folds into common + rare). Base (luck=0):
  // Legendary 3%, Rare 22%, Common 75%. At 50% luck ≈ Legendary 10% / Rare 30%.
  // At 100%+ (cap): Legendary 20% / Rare 42%.
  // BETA 20.77 — luck audit (Dylan at 341 luck: "luck doesnt really matter").
  // The old cap saturated at luck 125 — 216 of his 341 points were dead.
  // Cap raised 50 → 100 (saturation now at luck 250) + higher ceilings.
  const luckBonus = Math.min(luck * 0.4, 100);

  const legendaryChance = Math.min(3 + luckBonus * 0.34, 30);
  const rareChance = Math.min(22 + luckBonus * 0.4, 47);
  // Common fills the rest

  const roll = rollRng() * 100; // BETA 20.92 — seeded rarity roll

  if (roll < legendaryChance) return 'legendary';
  if (roll < legendaryChance + rareChance) return 'rare';
  return 'common';
}

// --- Generate Level-Up Options ---

/**
 * Generate the level-up upgrade options for the player to choose from.
 * This is called when the player levels up.
 * Options include: new percs (if slots available) and upgrades to existing percs.
 * @param luck - Player's current luck stat (affects rarity of offers)
 */
export function generateLevelUpOptions(
  state: PeanutPercRunState,
  luck: number = 0
): LevelUpOption[] {
  const options: LevelUpOption[] = [];
  const availablePool: Array<{ type: 'new_perc' | 'upgrade_perc'; perc: PeanutPerc; slotIndex: number }> = [];

  // BETA 15.9 — STRICT 4-PERC CAP per Dylan: "i already have 4 perks and when
  // i level up it is allowing me to choose more. but its fake. i can choose
  // them but i dont get the perk. i dont need to see any other perks beside
  // the one i chose to have on my character."
  //
  // Old behavior: when slots were full, new (un-equipped) percs were still
  // added to the pool with slotIndex=-1 ("replace weakest" semantics). They
  // appeared in the level-up UI but the actual select silently failed.
  //
  // New rules:
  //   - Empty slot exists → new_perc options offered to fill it
  //   - All 4 slots full  → ONLY upgrade_perc options offered (re-rolls of
  //                         the equipped 4 at random rarities, per the
  //                         15.7 jitter system)
  //
  // This matches Dylan's intent: "if i have 1 perk only i can see them all
  // if i have 2 perks i can see them all if i have 4 perks i just see all
  // the variations and possibilities of those perks."
  const emptySlotIndex = state.slots.findIndex(s => s.perc === null);
  const slotsAreFull = emptySlotIndex === -1;
  const equippedPercIds = new Set(state.slots.filter(s => s.perc).map(s => s.perc!.id));

  for (let i = 0; i < state.slots.length; i++) {
    const slot = state.slots[i];
    if (slot.perc && LEVELUP_EXCLUDED_PERC_IDS.has(slot.perc.id)) continue; // BETA 22.87 — no card art, never offered
    if (slot.perc && slot.level < slot.perc.maxLevel) {
      availablePool.push({ type: 'upgrade_perc', perc: slot.perc, slotIndex: i });
    }
  }

  // Only offer new_perc options when there's an empty slot to fill.
  if (!slotsAreFull) {
    const availableNewPercs = PEANUT_PERCS.filter(p =>
      !LEVELUP_EXCLUDED_PERC_IDS.has(p.id) &&   // BETA 22.87 — card-less percs (N13) retired from level-up
      state.unlockedPercIds.has(p.id) &&
      !equippedPercIds.has(p.id) &&
      !state.banishedPercIds.has(p.id)
    );
    for (const perc of availableNewPercs) {
      availablePool.push({ type: 'new_perc', perc, slotIndex: emptySlotIndex });
    }
  }

  // Shuffle helper
  function shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rollRng() * (i + 1)); // BETA 20.92 — seeded shuffle of offered options
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // BETA 16.20 — PSBOT cycle-23 P1.1: ANTI-REPEAT QUEUE. Filter the pool
  // against the last 6 percs offered across recent level-ups so players
  // can't tunnel-vision into one stat. If filtering reduces pool below
  // LEVEL_UP_OPTIONS_COUNT (3), allow repeats — don't soft-lock.
  // BETA 16.21 — PSBOT cycle-24 P0.4: queue size bumped 6 → 12 (= 4 level-
  // ups of memory at 3 cards each). Per Dylan: "im seeing a lot of the
  // same ones." Cycle-24 audit confirmed: 67% of his perc picks went to
  // T01+T02 with the 6-cap. 12-cap leaves ~11 of 23 unique percs eligible
  // per pick = healthy variety.
  const recentSet = new Set(state.recentlyOfferedPercs);
  const filteredPool = availablePool.filter(c => !recentSet.has(c.perc.id));
  // Use filtered pool ONLY if it has enough candidates for a full offer.
  // Otherwise fall back to the full pool (anti-repeat is a soft constraint).
  const workingPool = filteredPool.length >= LEVEL_UP_OPTIONS_COUNT
    ? filteredPool
    : availablePool;

  // BETA 16.24 — PSBOT cycle-25 P1.2: SOFT DE-PRIORITIZE 3+ STACKED PERCS.
  // Per cycle-25 audit: cowboy run T01+T02 = 64% of perc picks even with
  // the 12-cap queue + stat-cat dedup. Once a perc is at level 3+, give
  // it 30% weight by random-sorting it BEHIND fresh percs. Implementation:
  // duplicate non-3+-stacked candidates into the pool (effectively raising
  // their relative weight), then shuffle. Level-3+ candidates stay in
  // pool but get lower priority through random sort.
  const candidateLevel = (c: typeof workingPool[number]): number => {
    if (c.type === 'upgrade_perc' && c.slotIndex >= 0) {
      return state.slots[c.slotIndex].level;
    }
    return 0;
  };
  const lowPriorityPool = shuffle(workingPool.filter(c => candidateLevel(c) >= 3));
  const fullPriorityPool = shuffle(workingPool.filter(c => candidateLevel(c) < 3));

  // Reroll: prioritize percs NOT yet shown this level-up session, AND
  // weight full-priority above low-priority within each category.
  const freshFull = fullPriorityPool.filter(c => !state.offeredThisLevelUp.has(c.perc.id));
  const freshLow  = lowPriorityPool.filter(c => !state.offeredThisLevelUp.has(c.perc.id));
  const seenFull  = fullPriorityPool.filter(c =>  state.offeredThisLevelUp.has(c.perc.id));
  const seenLow   = lowPriorityPool.filter(c =>  state.offeredThisLevelUp.has(c.perc.id));
  // Order: fresh-full > fresh-low > seen-full > seen-low. Coin-flip on
  // each fresh-low candidate to actually keep it (30% pass rate when a
  // fresh-full alternative exists). If filling 3 slots requires dipping
  // into low-priority, take them anyway — soft, not hard, deprio.
  const orderedPool = [
    ...freshFull,
    ...(freshFull.length >= LEVEL_UP_OPTIONS_COUNT
        ? freshLow.filter(() => rollRng() < 0.3) // BETA 20.92 — seeded
        : freshLow),
    ...seenFull,
    ...seenLow,
  ];

  // Pick up to LEVEL_UP_OPTIONS_COUNT options (avoid duplicates of the same perc)
  // BETA 16.21 — PSBOT cycle-24 P0.4: stat-category SOFT-DEDUP. Don't offer
  // two cards from the same stat (e.g. two damage percs in one roll).
  // Soft = only enforced if the resulting pool still has enough variety.
  // Pre-pass: count how many distinct stats are available in workingPool.
  const distinctStats = new Set(orderedPool.map(c => c.perc.stat));
  const enforceCatDedup = distinctStats.size >= LEVEL_UP_OPTIONS_COUNT;
  const usedStats = new Set<string>();
  const usedPercIds = new Set<string>();
  const newlyOfferedIds: string[] = [];
  for (const candidate of orderedPool) {
    if (options.length >= LEVEL_UP_OPTIONS_COUNT) break;
    if (usedPercIds.has(candidate.perc.id)) continue;
    if (enforceCatDedup && usedStats.has(candidate.perc.stat)) continue;

    // BETA 22.87 — Perk Card System v1: 4-TIER card rarity (common/rare/epic/
    // legendary, weights in perkCards.ts) + card magnitude table (x1/x2/x3/x5).
    // rollUpgradeRarity/calculateUpgradeBonus stay for shrines + chests (3-tier).
    const rarity = rollCardRarity(luck);
    const bonusAmount = calculateCardBonus(candidate.perc, rarity, luck);
    const resultLevel = candidate.type === 'upgrade_perc' && candidate.slotIndex >= 0
      ? state.slots[candidate.slotIndex].level + 1
      : 1;

    options.push({
      type: candidate.type,
      perc: candidate.perc,
      rarity,
      bonusAmount,
      bonusDisplay: formatPercBonus(candidate.perc, bonusAmount),
      slotIndex: candidate.slotIndex,
      resultLevel,
      luckBoosted: luckValueMult(luck) > 1.001, // BETA 21.04 — 🍀 when luck raised it
    });

    usedPercIds.add(candidate.perc.id);
    usedStats.add(candidate.perc.stat);
    newlyOfferedIds.push(candidate.perc.id);
    // Track that this perc has been shown so next reroll skips it
    state.offeredThisLevelUp.add(candidate.perc.id);
  }
  // BETA 16.21 — Push the offered ids onto the recent queue (most recent
  // first) and trim to length. BETA 16.24 — bumped 12 → 18 (= 6 level-
  // ups of memory at 3 cards each) per cycle-25 audit (T01+T02 still
  // 64% of picks at 12-cap).
  state.recentlyOfferedPercs = [...newlyOfferedIds, ...state.recentlyOfferedPercs].slice(0, 18);

  state.isLevelUpActive = true;
  state.currentOptions = options;

  return options;
}

// --- Select a Level-Up Option ---

export interface SelectResult {
  success: boolean;
  message: string;
  percName: string;
  newLevel: number;
  bonusGained: number;
}

/**
 * Player selects one of the level-up options.
 * @param optionIndex - Index into state.currentOptions
 */
export function selectLevelUpOption(
  state: PeanutPercRunState,
  optionIndex: number
): SelectResult {
  if (!state.isLevelUpActive || !state.currentOptions) {
    return { success: false, message: 'No level-up active', percName: '', newLevel: 0, bonusGained: 0 };
  }

  if (optionIndex < 0 || optionIndex >= state.currentOptions.length) {
    return { success: false, message: 'Invalid option index', percName: '', newLevel: 0, bonusGained: 0 };
  }

  const option = state.currentOptions[optionIndex];

  if (option.type === 'new_perc') {
    let targetSlotIndex = option.slotIndex;
    // slotIndex -1: all slots full — replace the slot with the lowest accumulated bonus
    if (targetSlotIndex === -1) {
      let minBonus = Infinity;
      for (let i = 0; i < state.slots.length; i++) {
        if (!state.slots[i].perc || state.slots[i].accumulatedBonus < minBonus) {
          minBonus = state.slots[i].accumulatedBonus;
          targetSlotIndex = i;
        }
      }
    }
    if (targetSlotIndex < 0 || targetSlotIndex >= state.slots.length) {
      return { success: false, message: 'No slot available', percName: option.perc.name, newLevel: 0, bonusGained: 0 };
    }
    const slot = state.slots[targetSlotIndex];
    slot.perc = option.perc;
    slot.level = 1;
    slot.accumulatedBonus = option.bonusAmount;
    option.slotIndex = targetSlotIndex;
  } else {
    // Upgrade existing perc
    const slot = state.slots[option.slotIndex];
    if (!slot.perc || slot.perc.id !== option.perc.id) {
      return { success: false, message: 'Slot mismatch', percName: option.perc.name, newLevel: 0, bonusGained: 0 };
    }
    slot.level++;
    slot.accumulatedBonus += option.bonusAmount;
  }

  const newLevel = state.slots[option.slotIndex].level;
  // BETA 14.73 BUG-5: fire perc-tracker callback so bot/real-game picks count
  // toward percTracker.applies (was only firing from weaponLab.incrementPercLevel
  // before, which is the lab-randomizer path, leaving real picks invisible).
  _percApplyTracker?.(option.perc.id, newLevel);

  // BETA 15.31 — increment per-run rarity counter. Reads option.rarity (already
  // attached to every level-up offer at generation time) and increments the
  // matching bucket. Cross-reference at finalizeRun against finalLuckPct to
  // verify "is luck steering offers?" (higher luck → more legendary picks).
  _percPickTotal++;
  switch (option.rarity) {
    case 'common':    _percPickCommon++;    break;
    case 'uncommon':  _percPickUncommon++;  break;
    case 'rare':      _percPickRare++;      break;
    case 'epic':      _percPickEpic++;      break;
    case 'legendary': _percPickLegendary++; break;
  }

  // BETA 15.34 — fire the pick auditor with the picked option + ALL offered
  // alternatives BEFORE we clear state.currentOptions below. Engine handler
  // captures the audit entry with player stat snapshot.
  try { _percPickAuditor?.(option, [...(state.currentOptions ?? [])]); } catch { /* never crash level-up */ }

  // Clear level-up state — reset offered tracking for next level-up
  state.isLevelUpActive = false;
  state.currentOptions = null;
  state.offeredThisLevelUp = new Set();

  return {
    success: true,
    message: `${option.perc.name} → Level ${newLevel}!`,
    percName: option.perc.name,
    newLevel,
    bonusGained: option.bonusAmount,
  };
}

// --- Skip Level-Up (Reroll / Dismiss) ---

/**
 * Skip the current level-up without picking anything.
 * Some games allow this; we can gate it behind a special item.
 */
export function skipLevelUp(state: PeanutPercRunState): void {
  state.isLevelUpActive = false;
  state.currentOptions = null;
  state.offeredThisLevelUp = new Set(); // Reset for the next level-up
}

// --- Banish a Perc ---

/**
 * Banish a perc from appearing in future level-up options this run.
 * This increases the chance of getting percs you actually want.
 */
export function banishPerc(state: PeanutPercRunState, percId: string): boolean {
  const perc = getPercById(percId);
  if (!perc) return false;

  // Can't banish a perc that's already equipped
  const isEquipped = state.slots.some(s => s.perc?.id === percId);
  if (isEquipped) return false;

  state.banishedPercIds.add(percId);
  return true;
}

// --- Calculate All Perc Stat Bonuses ---

export interface PercRunBonuses {
  damage: number;          // multiplier (e.g., 0.48 = +48%)
  attackSpeed: number;
  maxHp: number;
  moveSpeed: number;
  dodgeChance: number;
  critChance: number;
  goldBonus: number;
  knockback: number;
  projectileSpeed: number;
  hpRegen: number;         // flat HP/sec
  shield: number;          // flat shield points
  xpBonus: number;
  attackSize: number;
  luck: number;            // flat luck points added to player.luck
  // BETA 15.13 — new stats opened by the new-perk redesign (PSBOT RUN spec)
  lifesteal: number;            // N01 — % of damage healed back
  armor: number;                // N02 — flat damage reduction
  thorns: number;               // N03 — % damage reflected to attacker
  critDamage: number;           // N04 — additive crit multiplier (e.g., 0.6 = +60% on crit)
  projectileCount: number;      // N05 — fractional, floored at apply
  overheal: number;             // N07 — flat overheal pool
  projectilePierce: number;     // N08 — fractional, floored at apply
  pickupRange: number;          // N09 — % of base pickup range
  eliteDamage: number;          // N11 — % bonus damage vs mini-bosses/elites
  projectileBounce: number;     // N12 — fractional, floored at apply
  phaseChance: number;          // N14 — % chance to phase contact damage
}

/**
 * Calculate the total stat bonuses from all equipped percs.
 * The engine should call this after every level-up selection
 * and apply the results to the player's stats.
 */
export function calculatePercRunBonuses(state: PeanutPercRunState): PercRunBonuses {
  const bonuses: PercRunBonuses = {
    damage: 0,
    attackSpeed: 0,
    maxHp: 0,
    moveSpeed: 0,
    dodgeChance: 0,
    critChance: 0,
    goldBonus: 0,
    knockback: 0,
    projectileSpeed: 0,
    hpRegen: 0,
    shield: 0,
    xpBonus: 0,
    attackSize: 0,
    luck: 0,
    // BETA 15.13 — new stats
    lifesteal: 0,
    armor: 0,
    thorns: 0,
    critDamage: 0,
    projectileCount: 0,
    overheal: 0,
    projectilePierce: 0,
    pickupRange: 0,
    eliteDamage: 0,
    projectileBounce: 0,
    phaseChance: 0,
  };

  for (const slot of state.slots) {
    if (!slot.perc || slot.level === 0) continue;
    // BETA 15.19 — Mechanic perks have a stat anchor for UI/type-system only.
    // Their bonus drives a custom mechanic (bungulator spawn, debt discount,
    // slow-on-hit-taken) read directly from slot.accumulatedBonus by engine
    // code. We MUST NOT add their accumulatedBonus to bonuses[stat] or it'll
    // silently buff the anchor stat (e.g. N10 stat='maxHp' was stealth-stacking
    // maxHp via BETA 15.18's new-stat apply lines).
    if (MECHANIC_PERK_IDS.has(slot.perc.id)) continue;
    const stat = slot.perc.stat;
    bonuses[stat] += slot.accumulatedBonus;
  }

  // BETA 17.15 — PSBOT cycle-30af: N05 Bungulazine at lvl 16 produced
  // 41-projectile god-mode (Cowboy floor 2 invincibility). Apply soft
  // cap: full value up to 15, then 50% DR past 15, then 25% past 25.
  // Curve:
  //   10 picks = 10 projectiles
  //   15 picks = 15 (unchanged)
  //   25 picks = 15 + 10*0.5 = 20
  //   41 picks (Cowboy) = 15 + 10*0.5 + 16*0.25 = 24 (was 41)
  // Still meaningful late-game scaling, no longer screen-clear god mode.
  if (bonuses.projectileCount > 15) {
    const raw = bonuses.projectileCount;
    let scaled = 15;
    const tier1 = Math.min(raw - 15, 10); // 15 → 25 at 50%
    scaled += tier1 * 0.5;
    if (raw > 25) {
      const tier2 = raw - 25;             // 25+ at 25%
      scaled += tier2 * 0.25;
    }
    bonuses.projectileCount = scaled;
  }

  // BETA 17.34 — PSBOT cycle-30aq Finding #11 (compound god-build closure).
  // Bungulazine projectileCount was capped in 17.15 but N08 Not Ritalin
  // (pierce) and N12 Mr Silly Pills (bounce) were left uncapped. At 20
  // picks of each, a single bullet pierces 20 enemies AND bounces 20
  // times = 400 effective hits per shot. × 24 projectiles from capped
  // Bungulazine = 9,600 hits per click. Apply the same 15/25-threshold
  // soft cap to both pierce and bounce.
  // Curve (matches projectileCount):
  //   15 picks = 15 (full)
  //   25 picks = 15 + 10×0.5 = 20
  //   40 picks = 15 + 10×0.5 + 15×0.25 = 23.75
  if (bonuses.projectilePierce > 15) {
    const raw = bonuses.projectilePierce;
    let scaled = 15;
    const tier1 = Math.min(raw - 15, 10);
    scaled += tier1 * 0.5;
    if (raw > 25) scaled += (raw - 25) * 0.25;
    bonuses.projectilePierce = scaled;
  }
  if (bonuses.projectileBounce > 15) {
    const raw = bonuses.projectileBounce;
    let scaled = 15;
    const tier1 = Math.min(raw - 15, 10);
    scaled += tier1 * 0.5;
    if (raw > 25) scaled += (raw - 25) * 0.25;
    bonuses.projectileBounce = scaled;
  }

  return bonuses;
}

// ─── BETA 15.19 — Mechanic perks ─────────────────────────────────────────
// N06 / N10 / N13 each have a `stat` field for UI/type-system compatibility
// but the per-pick bonus drives a custom mechanic, NOT a stat buff. Their
// cards must NOT show "+X.X <stat>" text (PSBOT cycle-15: "+0.0 Move Speed"
// bug + Dylan's rule "no card should ever say 0.0 and do nothing"), and
// their accumulatedBonus must NOT flow through calculatePercRunBonuses.
export const MECHANIC_PERK_IDS: ReadonlySet<string> = new Set(['N06', 'N10', 'N13']);

/**
 * Returns mechanic-specific card text for level-up offers + slot displays.
 * Returns null for non-mechanic perks (caller falls back to default stat-anchor render).
 *
 * @param percId  Perk id (e.g. 'N06')
 * @param rolled  The rolled per-pick value (slot.accumulatedBonus delta — only for level-up THIS PICK)
 * @param level   The level after this pick
 * @param accumulated  The total accumulated bonus across all picks (for TOTAL display)
 */
export function getMechanicCardText(
  percId: string,
  rolled: number,
  level: number,
  accumulated: number,
): { thisPick: string; total: string } | null {
  if (percId === 'N06') {
    // BETA 15.21 — every pick = +1 Bungulator buddy. NO CAP.
    // Per Dylan: "N06 should give you 1+ bungulator every level up." +
    // "not max!!!! unlimited"
    return {
      thisPick: '+1 Bungulator buddy',
      total: `${level} Bungulator${level === 1 ? '' : 's'}`,
    };
  }
  if (percId === 'N10') {
    // BETA 22.90 — HEART THROB (was Debt; gold mechanic deleted with the
    // token migration). One-time passive: own it = proximity shockwave.
    return {
      thisPick: 'Activates Heart Throb (passive)',
      total: 'HP critical → knockback shockwave (8s cd)',
    };
  }
  if (percId === 'N13') {
    // Not Xanax — slow attackers on hit-taken
    // Formula: slowDur = min(3.0s, 1.0 + 0.05 × accumulated)
    const slowDur = Math.min(3.0, 1.0 + 0.05 * accumulated);
    return {
      thisPick: `+${rolled.toFixed(2)} slow strength`,
      total: `Slow on hit-taken: ${slowDur.toFixed(2)}s @ 50% speed`,
    };
  }
  return null;
}

/**
 * Apply perc bonuses to player base stats.
 * Percentage bonuses are multiplied: finalStat = baseStat * (1 + percBonus)
 * Flat bonuses are added: finalStat = baseStat + percBonus
 */
export function applyPercBonusesToStats(
  baseStats: {
    baseMaxHp: number;
    baseDamage: number;
    baseMoveSpeed: number;
    baseAttackSpeed: number;
    baseCritChance: number;
    baseDodgeChance: number;
    baseHpRegen: number;
    baseXpGain: number;
    baseAttackRange: number;
    basePickupRange: number;
  },
  bonuses: PercRunBonuses
): typeof baseStats & { shield: number; goldBonus: number; knockback: number; projectileSpeed: number; attackSize: number } {
  // BETA 14.46 — DIVIDE BY 100 for percentage stats. perc.bonusPerLevel is
  // stored as integer percent (e.g., 1.5 means +1.5%/level), so accumulatedBonus
  // (= bonusPerLevel × level) is also integer percent (e.g., 9 for level 6 of
  // a +1.5%/level perc). The previous code treated `bonuses.damage` as if it
  // were a fraction (0.09 = 9%) and computed `baseDamage * (1 + 9) = 1000` —
  // which is 100× too large. Same bug for itemsAdapter consumer (lines 940-958);
  // see that fix in the same BETA. User: "felt invincible" — this was THE bug.
  return {
    ...baseStats,
    baseMaxHp: Math.round(baseStats.baseMaxHp * (1 + bonuses.maxHp / 100)),
    baseDamage: Math.round(baseStats.baseDamage * (1 + bonuses.damage / 100)),
    baseMoveSpeed: Math.round(baseStats.baseMoveSpeed * (1 + bonuses.moveSpeed / 100)),
    baseAttackSpeed: parseFloat((baseStats.baseAttackSpeed * (1 + bonuses.attackSpeed / 100)).toFixed(2)),
    baseCritChance: baseStats.baseCritChance + bonuses.critChance,
    baseDodgeChance: baseStats.baseDodgeChance + bonuses.dodgeChance,
    baseHpRegen: baseStats.baseHpRegen + bonuses.hpRegen,
    baseXpGain: parseFloat((baseStats.baseXpGain * (1 + bonuses.xpBonus / 100)).toFixed(2)),
    baseAttackRange: Math.round(baseStats.baseAttackRange * (1 + bonuses.attackSize / 100)),
    basePickupRange: baseStats.basePickupRange,
    shield: bonuses.shield,
    goldBonus: bonuses.goldBonus,
    knockback: bonuses.knockback,
    projectileSpeed: bonuses.projectileSpeed,
    attackSize: bonuses.attackSize,
  };
}

// --- Display Helpers ---

export interface PercSlotDisplay {
  slotIndex: number;
  isEmpty: boolean;
  percName: string;
  percIcon: string;
  level: number;
  maxLevel: number;
  stat: PercStat | '';
  totalBonusDisplay: string;
  accentColor: string;
  glowColor: string;
  description: string;
}

/**
 * Get display data for all perc slots (for HUD rendering).
 */
export function getPercSlotDisplays(state: PeanutPercRunState): PercSlotDisplay[] {
  return state.slots.map((slot, index) => {
    if (!slot.perc) {
      return {
        slotIndex: index,
        isEmpty: true,
        percName: 'Empty',
        percIcon: '',
        level: 0,
        maxLevel: 0,
        stat: '' as const,
        totalBonusDisplay: '',
        accentColor: '#444444',
        glowColor: '#22222266',
        description: '',
      };
    }

    // BETA 15.19 — Mechanic perks (N06/N10/N13) display custom totals, not
    // "+X.X <stat>" (their stat anchor is UI-only, not a real buff).
    const mechText = getMechanicCardText(slot.perc.id, 0, slot.level, slot.accumulatedBonus);
    const totalBonusDisplay = mechText
      ? mechText.total
      : formatPercBonus(slot.perc, slot.accumulatedBonus);

    return {
      slotIndex: index,
      isEmpty: false,
      percName: slot.perc.name,
      percIcon: slot.perc.iconUrl,
      level: slot.level,
      maxLevel: slot.perc.maxLevel,
      stat: slot.perc.stat,
      totalBonusDisplay,
      accentColor: slot.perc.accentColor,
      glowColor: slot.perc.glowColor,
      description: slot.perc.description,
    };
  });
}

const STAT_LABELS: Record<string, string> = {
  damage: 'Damage',
  attackSpeed: 'Attack Speed',
  maxHp: 'Max HP',
  moveSpeed: 'Move Speed',
  dodgeChance: 'Dodge Chance',
  critChance: 'Crit Chance',
  goldBonus: 'Gold Bonus',
  knockback: 'Knockback',
  projectileSpeed: 'Proj Speed',
  hpRegen: 'HP Regen',
  shield: 'Shield',
  xpBonus: 'XP Bonus',
  attackSize: 'Attack Size',
  luck: 'Luck',
};

export interface LevelUpOptionDisplay {
  optionIndex: number;
  type: 'new_perc' | 'upgrade_perc' | 'new_weapon' | 'weapon_upgrade';
  percName: string;
  percIcon: string;
  percDescription: string;
  rarity: UpgradeRarity;
  bonusDisplay: string;
  resultLevel: number;
  accentColor: string;
  glowColor: string;
  /** Set when type === 'new_weapon' or 'weapon_upgrade' */
  weaponId?: string;
  /** Funny one-liner from the perc */
  flavorText: string;
  /** Human-readable stat name e.g. "Attack Speed" */
  statLabel: string;
  /** Formatted bonus this pick gives e.g. "+1.5% Damage" */
  thisPickDisplay: string;
  /** Formatted running total after this pick e.g. "+4.5% Damage (3 stacks)" */
  newTotalDisplay: string;
  /** For weapon_upgrade cards: each upgraded stat as "Stat: before → after" */
  weaponStatLines?: string[];
  /** BETA 20.26 — structured per-pick stat delta(s) so the stats panel can
   *  project the hovered card's effect in real time. Each entry is the RAW
   *  per-pick bonus in the same units the player field uses (matching
   *  _applyPercStatBonus). Omitted for mechanic perks + weapon cards (their
   *  effect isn't a simple additive stat). */
  previewMods?: { stat: string; value: number; isPercent: boolean }[];
  /** BETA 21.04 — true when LUCK raised this card's number → draw a 🍀 badge. */
  luckBoosted?: boolean;
  /** BETA 22.87 — Perk Card System v1: underlying perc id (card art lookup). */
  percId?: string;
  /** BETA 22.87 — bottom small plate text (SURVIVAL / DAMAGE / ...). */
  category?: string;
  /** BETA 22.87 — the single-buff effect line for the bottom large box. */
  effectText?: string;
}

/**
 * Get display data for current level-up options (for level-up screen rendering).
 */
export function getLevelUpOptionDisplays(state: PeanutPercRunState): LevelUpOptionDisplay[] {
  if (!state.currentOptions) return [];

  return state.currentOptions.map((opt, index) => {
    const statLabel = STAT_LABELS[opt.perc.stat] || opt.perc.stat;

    // Find current slot data for this perc (null if not yet equipped)
    const slot = state.slots.find(s => s.perc?.id === opt.perc.id);
    const currentTotal = slot ? slot.accumulatedBonus : 0;
    const currentLevel = slot ? slot.level : 0;
    const newLevel = currentLevel + 1;
    const newTotal = currentTotal + opt.bonusAmount;

    // BETA 15.19 — Mechanic perks (N06/N10/N13) get custom card text. Default
    // stat-anchor render shows nonsense ("+0.0 Move Speed" for N06 because
    // bonusPerLevel=0 + stat='moveSpeed'; "+0.6 Max HP" for N10 because the
    // bonus is a Debt-discount unit, not maxHp). Dylan's rule: no card may
    // show "+0.0 anything" — and the text must reflect what the perk DOES.
    const mechText = getMechanicCardText(opt.perc.id, opt.bonusAmount, newLevel, newTotal);
    // BETA 16.26 — PSBOT cycle-27 P0.1 (the half-fix that completes
    // cycle-26). Per Dylan's screenshot: "Steroids THIS PICK: +91.3%
    // Damage" / "TOTAL (3 stacks): +299.3%" — but actual statsAtPick
    // showed +0.92 / +2.08. Display path here was multiplying integer-
    // percent bonusAmount by 100 (a holdover from when bonusAmount was
    // a fraction). Cycle-26 fixed `formatPercBonus` but missed this
    // second formatter on the LEVEL UP card. Both cards (THIS PICK and
    // TOTAL) now render the integer-percent value directly.
    const thisPickDisplay = mechText
      ? mechText.thisPick
      : (opt.perc.isPercent
        ? `+${opt.bonusAmount.toFixed(1)}% ${statLabel}`
        : `+${opt.bonusAmount.toFixed(1)} ${statLabel}`);

    // BETA 16.28 — PSBOT cycle-30d Ask 2 / Dylan: "its cool how it tells
    // you what you are going from and what you can go to if you chose
    // that item." Megabonk-style arrow format on the totalDisplay so
    // the player sees current → next at a glance instead of just the
    // post-pick total. Mechanic perks keep their custom text.
    const fmtVal = (v: number): string => opt.perc.isPercent
      ? `${v.toFixed(1)}%`
      : `${v.toFixed(1)}`;
    const newTotalStr = opt.perc.isPercent
      ? `${statLabel}: +${currentTotal.toFixed(1)}% → +${newTotal.toFixed(1)}%`
      : `${statLabel}: +${currentTotal.toFixed(1)} → +${newTotal.toFixed(1)}`;
    void fmtVal;
    const newTotalDisplay = mechText
      ? `${mechText.total}  (Lv ${currentLevel} → Lv ${newLevel})`
      : `${newTotalStr}  (Lv ${currentLevel} → Lv ${newLevel})`;

    // BETA 20.26 — structured projection data for the live stats-panel preview.
    // Only plain stat percs qualify: mechanic perks (mechText) anchor on a
    // misleading stat, and weapon cards don't map to a player stat field.
    const isStatPerc = !mechText && (opt.type === 'new_perc' || opt.type === 'upgrade_perc');
    const previewMods = isStatPerc
      ? [{ stat: opt.perc.stat, value: opt.bonusAmount, isPercent: opt.perc.isPercent }]
      : undefined;

    return {
      optionIndex: index,
      type: opt.type,
      percName: opt.perc.name,
      percIcon: opt.perc.iconUrl,
      percDescription: opt.perc.description,
      rarity: opt.rarity,
      bonusDisplay: opt.bonusDisplay,
      resultLevel: opt.resultLevel,
      accentColor: opt.perc.accentColor,
      glowColor: opt.perc.glowColor,
      flavorText: opt.perc.flavorText || '',
      statLabel,
      thisPickDisplay,
      newTotalDisplay,
      previewMods,
      luckBoosted: opt.luckBoosted, // BETA 21.04 — carry 🍀 flag to the card renderer
      // BETA 22.87 — Perk Card System v1 card fields. LV 1 = unlock text,
      // LV 2+ = the rolled one-buff line (Dylan: one effect per card, ever).
      percId: opt.perc.id,
      category: CARD_FAMILY_BY_PERC[opt.perc.id]?.category ?? 'UTILITY',
      effectText: opt.resultLevel <= 1
        ? (CARD_FAMILY_BY_PERC[opt.perc.id]?.unlockText ?? opt.perc.description)
        : (mechText ? mechText.thisPick : thisPickDisplay),
    };
  });
}

/**
 * Get XP bar display data.
 */
export function getXpBarDisplay(state: PeanutPercRunState): {
  level: number;
  currentXp: number;
  xpToNextLevel: number;
  progress: number; // 0-1
} {
  return {
    level: state.leveling.level,
    currentXp: state.leveling.currentXp,
    xpToNextLevel: state.leveling.xpToNextLevel,
    progress: state.leveling.currentXp / state.leveling.xpToNextLevel,
  };
}
